import React from "react";
import { Listbox } from "@headlessui/react";
import { Icon } from "@livingmap/hippo";

import { DropdownInterface } from "../FormControl";
import { getValueByPath } from "@utils";
import styles from "./Dropdown.module.scss";
import classNames from "classnames";

type Props = Omit<DropdownInterface, "type">;

const Dropdown: React.FC<Props> = ({
  dataQA,
  options,
  value,
  onChange,
  label,
  displayPath,
  disabled = false,
  error,
}) => {
  const isObject = typeof options[0] === "object";
  const hasError = Boolean(error);
  const errorId = hasError ? `${dataQA}-error` : undefined;

  if (isObject && !displayPath) {
    return (
      <p>
        Dropdown component needs a "displayPath" prop if the options consist of
        an array of objects/arrays
      </p>
    );
  }

  return (
    <div
      data-qa={dataQA}
      className={classNames(styles.container, {
        [styles.disabled]: disabled,
        [styles.hasError]: hasError,
      })}
    >
      <Listbox value={value} onChange={onChange}>
        {label && (
          <Listbox.Label
            className={classNames(styles.label, {
              [styles.errorLabel]: hasError,
            })}
          >
            {label}
          </Listbox.Label>
        )}
        <Listbox.Button
          className={classNames(styles.button, {
            [styles.errorButton]: hasError,
          })}
          aria-invalid={hasError}
          aria-describedby={errorId}
        >
          {isObject && displayPath
            ? getValueByPath(value, displayPath, "")
            : value}
          <Icon dataQA="chevron-down-icon" name="chevron_down" size={24} />
        </Listbox.Button>
        <div className={styles.panel}>
          <Listbox.Options className={styles.dropdown}>
            {options.map((item) => (
              <Listbox.Option
                key={
                  isObject && displayPath
                    ? getValueByPath(item, displayPath, "")
                    : item
                }
                value={item}
                as={React.Fragment}
              >
                {({ selected }) => (
                  <li
                    className={`${styles.dropdownItem} ${
                      selected ? styles.selected : ""
                    }`}
                  >
                    {isObject && displayPath
                      ? getValueByPath(item, displayPath, "")
                      : item}
                  </li>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
          <div className={styles.spacer} />
        </div>
      </Listbox>
      {hasError && (
        <span className={styles.errorText} id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
};

export default Dropdown;
